// TelcoTrack - Registration Component
// Handles user registration with form validation

import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../../../core/services/auth.service';
import {
  PASSWORD_POLICY_HINT,
  mapPasswordPolicyApiError,
  validatePasswordPolicy,
} from '../../../../core/utils/password-policy.util';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit {
  readonly passwordPolicyHint = PASSWORD_POLICY_HINT;

  registerForm: FormGroup;
  isLoading = false;
  errorMessage = '';
  successMessage = '';

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService
  ) {
    this.registerForm = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required, this.passwordPolicyValidator]),
      confirmPassword: new FormControl('', [Validators.required])
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/maps']);
    }
  }

  private passwordPolicyValidator(control: AbstractControl): ValidationErrors | null {
    const value = typeof control.value === 'string' ? control.value : '';
    if (!value) return null;
    const result = validatePasswordPolicy(value);
    return result.isValid ? null : { passwordPolicy: result.errors[0] };
  }

  passwordMatchValidator(control: AbstractControl) {
    const form = control as FormGroup;
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ ...(confirmPassword.errors ?? {}), passwordMismatch: true });
      return { passwordMismatch: true };
    }

    if (confirmPassword?.errors?.['passwordMismatch']) {
      const { passwordMismatch: _removed, ...rest } = confirmPassword.errors;
      confirmPassword.setErrors(Object.keys(rest).length ? rest : null);
    }

    return null;
  }

  async onSubmit(): Promise<void> {
    if (this.registerForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      const formData = this.registerForm.value;
      const userData = {
        email: formData.email,
        password: formData.password
      };

      try {
        await firstValueFrom(this.authService.register(userData));
        this.successMessage = 'Registrace byla úspěšná! Nyní se můžete přihlásit.';
        this.registerForm.reset();

        setTimeout(() => {
          this.router.navigate(['/auth/login']);
        }, 2000);
      } catch (error: any) {
        const msg = error?.error?.message ?? error?.message;
        const is403 = error?.status === 403 || msg?.includes('administrator');
        this.errorMessage = is403
          ? 'Pro registraci musí být váš účet zřízen administrátorem. Kontaktujte prosím administratora.'
          : (msg ? mapPasswordPolicyApiError(String(msg)) : 'Registrace se nezdařila. Zkuste to prosím znovu.');
      } finally {
        this.isLoading = false;
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.registerForm.controls).forEach(key => {
      const control = this.registerForm.get(key);
      control?.markAsTouched();
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.registerForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return 'Toto pole je povinné';
      }
      if (field.errors['email']) {
        return 'Zadejte platnou emailovou adresu';
      }
      if (field.errors['passwordPolicy']) {
        return String(field.errors['passwordPolicy']);
      }
      if (field.errors['passwordMismatch']) {
        return 'Hesla se neshodují';
      }
    }
    return '';
  }
}
